/* Scaffold dialog — create new custom template from system default */

import { state, refs, esc } from './state.js';
import { api } from './api.js';
import { loadFileTree, loadSyncStatuses } from './file-tree.js';
import { openFileTab } from './search.js';

let slotsData = null; // cached {pages: {PageName: [{id, name}]}}

export async function openScaffoldDialog() {
  refs.scaffoldDialog.showModal();
  refs.scaffoldError.hidden = true;
  refs.scaffoldPreview.hidden = true;
  refs.scaffoldName.value = "";
  refs.scaffoldSubmit.disabled = false;

  // Load slots if not cached
  if (!slotsData) {
    refs.scaffoldPage.innerHTML = '<option>Loading...</option>';
    refs.scaffoldSlot.innerHTML = '<option>\u2014</option>';
    try {
      slotsData = await api.templateSlots();
    } catch (e) {
      refs.scaffoldError.textContent = `Failed to load template slots: ${e.message}`;
      refs.scaffoldError.hidden = false;
      return;
    }
  }

  // Populate page dropdown
  const pages = Object.keys(slotsData.pages).sort();
  refs.scaffoldPage.innerHTML = pages.map(p =>
    `<option value="${esc(p)}">${esc(p)}</option>`
  ).join("");
  updateSlotDropdown();
}

function updateSlotDropdown() {
  const page = refs.scaffoldPage.value;
  const slots = (slotsData?.pages[page] || []).sort((a, b) => a.name.localeCompare(b.name));
  refs.scaffoldSlot.innerHTML = slots.map(s =>
    `<option value="${esc(s.name)}">${esc(s.name)}</option>`
  ).join("");
  updatePreview();
}

function updatePreview() {
  const page = refs.scaffoldPage.value;
  const slot = refs.scaffoldSlot.value;
  const name = refs.scaffoldName.value.trim();
  if (page && slot && name) {
    const slotParts = slot.split("/");
    const dir = slotParts.length > 1 ? slotParts.slice(0, -1).join("/") + "/" : "";
    refs.scaffoldPreview.textContent = `\u2192 ui-templates/${page}/${dir}${name}.html`;
    refs.scaffoldPreview.hidden = false;
  } else {
    refs.scaffoldPreview.hidden = true;
  }
}

async function handleScaffoldSubmit() {
  const page = refs.scaffoldPage.value;
  const slot = refs.scaffoldSlot.value;
  const name = refs.scaffoldName.value.trim();

  if (!name) {
    refs.scaffoldError.textContent = "Please enter a template name.";
    refs.scaffoldError.hidden = false;
    return;
  }

  refs.scaffoldSubmit.disabled = true;
  refs.scaffoldError.hidden = true;

  try {
    const result = await api.scaffoldTemplate(page, slot, name);
    if (result.status === "error") {
      refs.scaffoldError.textContent = result.reason;
      refs.scaffoldError.hidden = false;
      return;
    }
    refs.scaffoldDialog.close();
    await loadFileTree();
    await loadSyncStatuses();
    // Open the new custom file in a workspace tab
    const tenant = state.tenant || "";
    let relPath = result.custom_path;
    const tenantPrefix = tenant + "/";
    if (relPath.includes(tenantPrefix)) {
      relPath = relPath.substring(relPath.indexOf(tenantPrefix) + tenantPrefix.length);
    }
    openFileTab(relPath, `${name}.html`, "html");
  } catch (e) {
    refs.scaffoldError.textContent = `Failed: ${e.message}`;
    refs.scaffoldError.hidden = false;
  } finally {
    refs.scaffoldSubmit.disabled = false;
  }
}

export function initScaffold() {
  if (!refs.scaffoldDialog) return;
  refs.scaffoldPage.addEventListener("change", updateSlotDropdown);
  refs.scaffoldSlot.addEventListener("change", updatePreview);
  refs.scaffoldName.addEventListener("input", updatePreview);
  refs.scaffoldCancel.addEventListener("click", () => refs.scaffoldDialog.close());
  refs.scaffoldSubmit.addEventListener("click", handleScaffoldSubmit);
  refs.scaffoldDialog.addEventListener("click", (e) => {
    if (e.target === refs.scaffoldDialog) refs.scaffoldDialog.close();
  });
  refs.scaffoldDialog.addEventListener("keydown", (e) => {
    if (e.key === "Escape") refs.scaffoldDialog.close();
    if (e.key === "Enter") {
      e.preventDefault();
      handleScaffoldSubmit();
    }
  });
}
